<template>
  <div class="panel engagement-documents" data-qid="engagement-documents">
    <header class="panel__header">
      <div class="profile-compact">
        <div class="logo-container">
          <font-awesome-icon icon="folder-open" class="icon-svg--sm"/>
        </div>
        <div class="profile-compact__info">
          <div class="profile-compact__top">
            <div class="profile-compact__text">
              {{ $t('projects.engagement.documents.title') }}
              <span class="section-compact__secondary">{{ $t('projects.engagement.documents.count', { count: allFiles.length }) }}</span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="panel__body">
      <div class="docs-layout">
      <!-- Folders: phases with their steps, working papers under Execution, and the other document types -->
      <nav class="docs-rail" data-qid="engagement-documents-folders">
        <div class="docs-folder"
             :class="{'docs-folder--on': !selectedGroup}"
             @click="selectFolder(null, null)"
             data-qid="engagement-documents-folder-all"
        >
          <font-awesome-icon icon="folder-open" class="mr-2"/>
          <span class="docs-folder__name">{{ $t('projects.engagement.documents.all_groups') }}</span>
          <span class="docs-folder__count">{{ allFiles.length }}</span>
        </div>
        <template v-for="group in folderGroups">
          <div :key="'folder-' + group.key"
               class="docs-folder"
               :class="{'docs-folder--on': selectedGroup === group.key && !selectedSection}"
               @click="selectFolder(group.key, null)"
               :data-qid="'engagement-documents-folder-' + group.key"
          >
            <font-awesome-icon :icon="group.icon" class="mr-2"/>
            <span class="docs-folder__name">{{ group.title }}</span>
            <span class="docs-folder__count">{{ group.count }}</span>
          </div>
          <div v-for="section in group.sections"
               :key="'folder-' + group.key + '-' + section.key"
               class="docs-folder docs-folder--sub"
               :class="{'docs-folder--on': selectedGroup === group.key && selectedSection === section.key}"
               @click="selectFolder(group.key, section.key)"
               :data-qid="'engagement-documents-folder-' + group.key + '-' + section.key"
          >
            <span class="docs-folder__name">{{ section.title }}</span>
            <span class="docs-folder__count">{{ section.count }}</span>
          </div>
        </template>
      </nav>

      <div class="docs-main">
      <div class="engagement-documents__toolbar">
        <b-form-input v-model="keyword"
                      :placeholder="$t('projects.engagement.documents.search_placeholder')"
                      class="engagement-documents__search"
                      data-qid="engagement-documents-search"
        ></b-form-input>
        <span class="docs-chip"
              :class="{'docs-chip--on': quickFilter === 'not_linked'}"
              @click="toggleQuickFilter('not_linked')"
              data-qid="engagement-documents-filter-not-linked"
        >{{ $t('projects.engagement.documents.filter_not_linked') }} <b v-if="notLinkedCount">{{ notLinkedCount }}</b></span>
        <span class="docs-chip"
              :class="{'docs-chip--on': quickFilter === 'not_signed_off'}"
              @click="toggleQuickFilter('not_signed_off')"
              data-qid="engagement-documents-filter-not-signed-off"
        >{{ $t('projects.engagement.documents.filter_not_signed_off') }}</span>
      </div>

      <!-- Upload: pick where the files go, then add them -->
      <div class="docs-upload" v-if="uploadOptions.length">
        <font-awesome-icon icon="cloud-upload-alt" class="docs-upload__icon"/>
        <span class="docs-upload__label">{{ $t('projects.engagement.documents.upload_to') }}</span>
          <b-form-select v-model="uploadTarget"
                         :options="uploadOptions"
                         class="engagement-documents__filter"
                         data-qid="engagement-documents-upload-target"></b-form-select>
          <!-- Working papers are kept on a main task -->
          <multiselect v-if="uploadTarget === workingPaperTarget"
                       v-model="workingPaperTask"
                       :options="evidenceTasks"
                       track-by="id"
                       label="title"
                       select-label=""
                       selected-label=""
                       deselect-label=""
                       :placeholder="$t('projects.engagement.documents.working_paper_task')"
                       class="engagement-documents__task"
                       data-qid="engagement-documents-working-paper-task"
          ></multiselect>
          <file-upload v-if="uploadTarget !== workingPaperTarget || workingPaperTask"
              ref="documents-uploader"
              v-model="files"
              input-id="engagement-documents-upload"
              :multiple="true"
              :drop="true"
              @input="upload"
              class="btn btn-success btn-sm engagement-documents__upload-button"
              data-qid="engagement-documents-upload"
          >
            <font-awesome-icon icon="cloud-upload-alt" class="mr-1"/>
            {{ $t('projects.engagement.step_ui.upload') }}
          </file-upload>
      </div>

      <div v-for="group in pagedGroups" :key="'doc-group-' + group.key" class="engagement-documents__group">
        <div class="engagement-documents__group-title">
          {{ group.title }}
          <span class="text-muted ml-1">({{ group.total }})</span>
        </div>
        <div v-for="section in group.sections" :key="'doc-section-' + group.key + '-' + section.key">
          <div v-if="section.title" class="engagement-documents__section-title">
            {{ section.title }}
            <span class="text-muted ml-1">({{ section.total }})</span>
          </div>
          <div v-for="file in section.files" :key="'doc-' + (file.workingPaper ? 'wp-' : '') + file.id" class="engagement-documents__file">
            <!-- Shows how the file opens: with its annotations, or as the plain file -->
            <font-awesome-icon v-if="opensAnnotated(file)"
                               icon="highlighter"
                               class="attachment-state-icon attachment-state-icon--annotated mr-2"
                               v-b-tooltip.hover
                               :title="$t('annotations.opens_annotated')"
                               data-qid="engagement-document-annotated-icon"/>
            <font-awesome-icon v-else icon="paperclip" class="text-muted mr-2"/>
            <a href="#"
               @click.prevent="openDocument(file)"
               class="text-truncate engagement-documents__file-name"
               data-qid="engagement-document-file">{{ file.file_name }}</a>
            <span class="engagement-documents__file-step text-muted text-truncate" v-if="file.workingPaper">
              {{ $t('projects.engagement.documents.working_paper') }}
            </span>
            <span class="engagement-documents__file-step text-muted text-truncate" v-else-if="file.stepLabel">
              {{ $t('projects.engagement.step.' + file.stepLabel) }}
            </span>
            <span class="engagement-documents__file-step text-muted text-truncate" v-else>
              {{ $t('projects.engagement.documents.unlinked') }}
            </span>
            <!-- Tasks of this project the file is attached or linked to -->
            <span class="engagement-documents__file-tasks">
              <a v-if="taskInfo(file)"
                 href="#"
                 class="document-tasks-pill"
                 :class="{'document-tasks-pill--none': !taskCount(file)}"
                 @click.prevent="$refs.documentTasks.show(taskInfo(file))"
                 data-qid="engagement-document-tasks"
              >
                <font-awesome-icon icon="tasks" class="mr-1"/>{{ $t('projects.engagement.evidence.linked_count', { count: taskCount(file) }) }}
              </a>
              <span v-else class="text-muted">–</span>
            </span>
            <!-- Sign-off, with the same rules as tasks -->
            <span class="engagement-documents__file-sign-off" data-qid="engagement-document-sign-off">
              <template v-if="isProjectFile(file)">
                <span v-if="signOffState(file).rejected"
                      class="document-sign-off-pill document-sign-off-pill--rejected"
                      v-b-tooltip.hover
                      :title="rejectedTooltip(file)"
                >
                  <font-awesome-icon icon="times-circle" class="mr-1"/>{{ $t('system.rejected') }}
                </span>
                <span v-else-if="signOffState(file).signers.length"
                      class="document-sign-off-pill"
                      :class="{'document-sign-off-pill--accepted': isAccepted(file)}"
                      v-b-tooltip.hover
                      :title="signersTooltip(file)"
                >
                  <font-awesome-icon :icon="isAccepted(file) ? 'check-circle' : 'exclamation-triangle'" class="mr-1"/>
                  {{ $t('system.signed_off') }} ({{ signOffState(file).signers.length }}/{{ requiredSignOffs }})
                </span>
                <span v-else class="text-muted">{{ $t('projects.engagement.documents.not_signed_off') }}</span>
              </template>
            </span>
            <!-- Who added the file and when (US 9477) -->
            <span class="engagement-documents__file-date text-muted"
                  v-b-tooltip.hover
                  :title="addedLine(file)"
                  data-qid="engagement-document-added"
            >
              <span class="engagement-documents__file-by" v-if="file.added_by">{{ file.added_by.name || $t('attachments.deleted_user') }}</span>
              <span>{{ (file.added_at || file.created_at) | formatDateTime('DD MMM YYYY HH:mm') }}</span>
            </span>
            <span class="engagement-documents__file-actions">
              <template v-if="canSignOff && isProjectFile(file)">
                <span v-if="!isSigner(file)"
                      class="cursor-pointer text-muted mr-3"
                      @click="signOff(file, 'accepted')"
                      v-b-tooltip.hover
                      :title="$t('projects.engagement.documents.sign_off')"
                      data-qid="engagement-document-accept">
                  <font-awesome-icon icon="check-circle"/>
                </span>
                <span class="cursor-pointer text-muted mr-3"
                      @click="showRejectModal(file)"
                      v-b-tooltip.hover
                      :title="$t('projects.engagement.documents.reject')"
                      data-qid="engagement-document-reject">
                  <font-awesome-icon icon="times-circle"/>
                </span>
              </template>
              <span v-if="canAnnotate(file)"
                    class="cursor-pointer text-muted mr-3"
                    @click="annotate(file)"
                    v-b-tooltip.hover
                    :title="$t('annotations.annotate')"
                    data-qid="engagement-document-annotate">
                <font-awesome-icon icon="highlighter"/>
              </span>
              <span class="cursor-pointer text-muted mr-3"
                    @click="downloadMedia(file)"
                    v-b-tooltip.hover
                    :title="$t('projects.engagement.documents.download')">
                <font-awesome-icon icon="cloud-download-alt"/>
              </span>
              <span v-if="canDelete(file)"
                    class="cursor-pointer text-muted"
                    @click="openDeleteModal(file)"
                    v-b-tooltip.hover
                    :title="$t('attachments.delete.title')"
                    data-qid="engagement-document-remove">
                <font-awesome-icon icon="trash-alt"/>
              </span>
            </span>
          </div>
        </div>
      </div>

      <div class="mt-3 pagination row" v-if="visibleRows.length" data-qid="engagement-documents-pagination">
        <div class="col-md-6">
          <div class="row">
            <div class="float-left">
              <span class="text-muted pagination-counter">
                {{ $t('pagination.entries_per_page') }}:
              </span>
            </div>
            <div class="float-left">
              <multiselect
                  v-model="selectedPerPage"
                  :options="perPageOptions"
                  track-by="value"
                  label="name"
                  :allow-empty="false"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="''"
                  @select="currentPage = 1"
                  data-qid="engagement-documents-per-page"
              ></multiselect>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <b-pagination
              class="float-right"
              v-model="currentPage"
              :total-rows="visibleRows.length"
              :per-page="perPage"
              limit="1"
          >
            <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
            <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
            <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
            <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
            <span class="text-muted" slot="page" slot-scope="{ active }">
              <span v-if="active">{{ pageCounter }}</span>
            </span>
          </b-pagination>
        </div>
      </div>

      <div v-if="!visibleGroups.length" class="text-muted section-container" data-qid="engagement-documents-empty">
        {{ $t('projects.engagement.documents.empty') }}
      </div>
      </div>
      </div>
    </div>

    <DocumentTasksModal ref="documentTasks"
                        :project="project"
                        :tasks="evidenceTasks"
                        :editable="!!access.edit && isOpen"
                        @changed="loadEvidence"
    ></DocumentTasksModal>

    <b-modal ref="reject-document-modal" modal-class="task-modal">
      <template #modal-header>
        <div class="" style="width: 100%;">
          <span @click="$refs['reject-document-modal'].hide()" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center">{{ $t('projects.engagement.documents.reject') }}</h5>
        </div>
      </template>
      <p class="text-muted">{{ rejectFile.file_name }}</p>
      <label>{{ $t('system.rejection_comment') }}</label>
      <b-form-textarea v-model="rejectComment" rows="4" data-qid="engagement-document-reject-comment"></b-form-textarea>
      <template #modal-footer>
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-right">
              <b-button variant="secondary" @click="$refs['reject-document-modal'].hide()">{{ $t('projects.create.cancel') }}</b-button>
              <b-button class="ml-3"
                        variant="danger"
                        :disabled="buttonDisabled"
                        @click="signOff(rejectFile, 'rejected', rejectComment)"
                        data-qid="engagement-document-reject-submit"
              >
                {{ $t('projects.engagement.documents.reject') }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <DocumentAnnotationViewer ref="annotationViewer" @closed="loadAnnotationCounts"></DocumentAnnotationViewer>

    <ConfirmRemoveAttachment
        :object="selectedFile"
        :show="showDeleteModal"
        type="file"
        @closeConfirmModal="closeDeleteModal"
    ></ConfirmRemoveAttachment>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
import DocumentTasksModal from '@/views/Projects/Engagement/DocumentTasksModal.vue';
import DocumentAnnotationViewer from '@/components/annotations/DocumentAnnotationViewer.vue';
import { isAnnotatable } from '@/components/annotations/annotations.js';
import { documentAdded } from '@/util/documentAdded.js';
import { openDocument as openDocumentInTab } from '@/util/openDocument.js';
import { PHASE_KEYS, PHASES, COLLECTION_OTHER, STEPS_WITHOUT_DOCUMENTS, collectionOf, phaseOf, canEditPhase, engagementPermissions } from '@/views/Projects/Engagement/engagement.js';

// Upload target for working papers, which are uploaded to a main task instead of the project
const WORKING_PAPER_TARGET = 'working_papers';

// Collections that existed before the engagement overview, shown in their own groups
const LEGACY_GROUPS = [
  { key: 'system_description', collection: 'system_description', label: 'projects.engagement.documents.system_description' },
  { key: 'report', collection: 'report', label: 'projects.engagement.documents.reports' },
  { key: 'attachments', collection: 'attachments', label: 'projects.engagement.documents.exports' }
];

export default {
  name: 'EngagementDocuments',
  props: {
    project: {
      type: Object,
      required: true
    },
    engagement: {
      type: Object,
      required: true
    }
  },
  components: {
    ConfirmRemoveAttachment,
    DocumentTasksModal,
    DocumentAnnotationViewer
  },
  data: function() {
    return {
      keyword: '',
      selectedGroup: null,
      selectedSection: null,
      quickFilter: null,
      uploadTarget: COLLECTION_OTHER,
      files: [],
      selectedFile: {},
      showDeleteModal: false,
      rejectFile: {},
      evidenceFiles: [],
      workingPapers: [],
      evidenceTasks: [],
      rejectComment: '',
      buttonDisabled: false,
      workingPaperTarget: WORKING_PAPER_TARGET,
      workingPaperTask: null,
      // Annotations per file (media id), so viewing an annotated file shows its annotations
      annotationCounts: {},
      currentPage: 1,
      perPageOptions: [
        { name: '10', value: 10 },
        { name: '20', value: 20 },
        { name: '50', value: 50 },
        { name: '100', value: 100 }
      ],
      selectedPerPage: { name: '10', value: 10 }
    }
  },
  computed: {
    access() {
      return engagementPermissions(this.project);
    },
    isOpen() {
      return this.project.status !== 'closed' && this.project.status !== 'completed';
    },
    canSignOff() {
      return !!this.access.sign_off && this.isOpen;
    },
    requiredSignOffs() {
      return Math.max(1, parseInt(this.project.required_sign_offs, 10) || 0);
    },
    allFiles() {
      const stepByCollection = {};
      _.forEach(PHASE_KEYS, phase => {
        _.forEach(PHASES[phase], step => {
          stepByCollection[collectionOf(step)] = step;
        });
      });

      const known = [COLLECTION_OTHER].concat(_.keys(stepByCollection), _.map(LEGACY_GROUPS, 'collection'));

      return _.map(_.filter(this.project.media || [], file => known.includes(file.collection_name)), file => {
        const step = stepByCollection[file.collection_name] || null;
        // Uploaded and generated reports belong to the Reporting step
        const stepLabel = step || (file.collection_name === 'report' ? 'reporting' : null);
        return Object.assign({}, file, { step: step, stepLabel: stepLabel, phase: step ? phaseOf(step) : null });
      }).concat(
        // Documentation collection files uploaded directly to tasks are evidence too
        _.map(_.filter(this.evidenceFiles, file => file.source === 'task'), file => Object.assign({}, file, {
          collection_name: 'data_documents', step: 'evidence_collection', stepLabel: 'evidence_collection', phase: 'execution', fromTask: true
        })),
        // Auditors' working papers on the main tasks
        _.map(this.workingPapers, file => Object.assign({}, file, {
          collection_name: 'documents', step: null, stepLabel: null, phase: 'execution', fromTask: true, workingPaper: true
        }))
      );
    },
    groups() {
      // Phases are split by step; working papers sit in Execution next to Evidence collection
      let groups = _.map(PHASE_KEYS, phase => {
        const files = _.filter(this.allFiles, file => file.phase === phase);
        let sections = _.map(PHASES[phase], step => ({
          key: step,
          title: this.$t('projects.engagement.step.' + step),
          files: _.filter(files, file => !file.workingPaper && file.step === step)
        }));
        if (phase === 'execution') {
          sections.push({
            key: 'working_papers',
            title: this.$t('projects.engagement.documents.working_papers'),
            files: _.filter(files, file => file.workingPaper)
          });
        }
        return {
          key: phase,
          title: this.$t('projects.engagement.phase.' + phase),
          sections: sections
        };
      });

      _.forEach(LEGACY_GROUPS, group => {
        groups.push({
          key: group.key,
          title: this.$t(group.label),
          sections: [{ key: group.key, title: null, files: _.filter(this.allFiles, file => file.collection_name === group.collection) }]
        });
      });

      groups.push({
        key: 'other',
        title: this.$t('projects.engagement.documents.other'),
        sections: [{ key: 'other', title: null, files: _.filter(this.allFiles, file => file.collection_name === COLLECTION_OTHER) }]
      });

      return groups;
    },
    visibleGroups() {
      const keyword = this.keyword.trim().toLowerCase();
      const matches = file => (!keyword || (file.file_name || '').toLowerCase().includes(keyword)) && this.passesQuickFilter(file);

      return _.filter(_.map(this.groups, group => {
        const sections = _.filter(_.map(group.sections, section => Object.assign({}, section, {
          files: _.orderBy(_.filter(section.files, matches), [file => file.added_at || file.created_at], ['desc'])
        })), section => section.files.length && (!this.selectedSection || this.selectedGroup !== group.key || section.key === this.selectedSection));
        return Object.assign({}, group, { sections: sections, total: _.sumBy(sections, section => section.files.length) });
      }), group => group.total && (!this.selectedGroup || this.selectedGroup === group.key));
    },
    // One row per file, in display order, so the list can be paginated across groups
    visibleRows() {
      let rows = [];
      _.forEach(this.visibleGroups, group => {
        _.forEach(group.sections, section => {
          _.forEach(section.files, file => rows.push({ group: group, section: section, file: file }));
        });
      });
      return rows;
    },
    perPage() {
      return this.selectedPerPage ? this.selectedPerPage.value : this.perPageOptions[0].value;
    },
    // Groups and sections of the current page; their counts stay the full totals
    pagedGroups() {
      const start = (this.currentPage - 1) * this.perPage;
      let groups = [];
      _.forEach(this.visibleRows.slice(start, start + this.perPage), row => {
        let group = _.last(groups);
        if (!group || group.key !== row.group.key) {
          group = { key: row.group.key, title: row.group.title, total: row.group.total, sections: [] };
          groups.push(group);
        }
        let section = _.last(group.sections);
        if (!section || section.key !== row.section.key) {
          section = { key: row.section.key, title: row.section.title, total: row.section.files.length, files: [] };
          group.sections.push(section);
        }
        section.files.push(row.file);
      });
      return groups;
    },
    pageCounter() {
      const from = (this.currentPage - 1) * this.perPage + 1;
      const to = Math.min(this.currentPage * this.perPage, this.visibleRows.length);
      return from + ' - ' + to + ' ' + this.$t('system.of') + ' ' + this.visibleRows.length;
    },
    // Folders for the rail, with counts; steps without files are left out
    folderGroups() {
      const icons = { acceptance: 'file-signature', planning: 'tasks', execution: 'check-circle', reporting: 'file-alt', system_description: 'desktop', report: 'file-alt', attachments: 'file-download', other: 'folder' };
      return _.map(this.groups, group => {
        const sections = _.filter(_.map(group.sections, section => ({ key: section.key, title: section.title, count: section.files.length })), section => section.title && section.count);
        return {
          key: group.key,
          title: group.title,
          icon: icons[group.key] || 'folder',
          count: _.sumBy(group.sections, section => section.files.length),
          sections: sections
        };
      });
    },
    // Evidence files that aren't linked to any task
    notLinkedCount() {
      return _.filter(this.allFiles, file => this.isNotLinked(file)).length;
    },
    groupFilterOptions() {
      return [{ value: null, text: this.$t('projects.engagement.documents.all_groups') }].concat(_.map(this.groups, group => ({ value: group.key, text: group.title })));
    },
    uploadOptions() {
      if (!this.isOpen || !this.access.edit) {
        return [];
      }

      let options = [{ value: COLLECTION_OTHER, text: this.$t('projects.engagement.documents.other') }];
      const included = this.engagement.included_steps || [];

      _.forEach(PHASE_KEYS, phase => {
        if (!canEditPhase(this.project, phase)) {
          return;
        }
        _.forEach(PHASES[phase], step => {
          if (included.includes(step) && !STEPS_WITHOUT_DOCUMENTS.includes(step)) {
            options.push({ value: collectionOf(step), text: this.$t('projects.engagement.phase_short.' + phase) + ' · ' + this.$t('projects.engagement.step.' + step) });
          }
        });
        // Working papers are listed under Execution, next to Evidence collection
        if (phase === 'execution' && this.evidenceTasks.length) {
          options.push({ value: WORKING_PAPER_TARGET, text: this.$t('projects.engagement.phase_short.' + phase) + ' · ' + this.$t('projects.engagement.documents.working_papers') });
        }
      });

      return options;
    }
  },
  created() {
    this.loadEvidence();
  },
  watch: {
    'project.media'() {
      this.loadEvidence();
    },
    keyword() {
      this.currentPage = 1;
    },
    selectedGroup() {
      this.currentPage = 1;
    },
    selectedSection() {
      this.currentPage = 1;
    },
    // Stay on a page that still exists after files are removed
    visibleRows(rows) {
      const lastPage = Math.max(1, Math.ceil(rows.length / this.perPage));
      if (this.currentPage > lastPage) {
        this.currentPage = lastPage;
      }
    }
  },
  methods: {
    addedLine(file) {
      return documentAdded(file, this.$t.bind(this));
    },
    loadEvidence() {
      let self = this;
      piincHttp.get('projects/' + this.project.slug + '/evidence').then(function(response) {
        self.evidenceFiles = response.data.files;
        self.workingPapers = response.data.working_papers;
        self.evidenceTasks = response.data.tasks;
      }, function() {});
      this.loadAnnotationCounts();
    },
    loadAnnotationCounts() {
      let self = this;
      piincHttp.get('projects/' + this.project.slug + '/annotations/counts').then(function(response) {
        self.annotationCounts = response.data.counts || {};
      }, function() {});
    },
    // Project files are signed off on the Documents tab; files on tasks follow the task's own review
    isProjectFile(file) {
      return !file.fromTask && file.collection_name !== 'attachments';
    },
    // Task links of evidence files and the task of task files; null for documents that don't belong to tasks
    selectFolder(group, section) {
      this.selectedGroup = group;
      this.selectedSection = section;
    },
    toggleQuickFilter(filter) {
      this.quickFilter = this.quickFilter === filter ? null : filter;
      this.currentPage = 1;
    },
    isNotLinked(file) {
      const info = this.taskInfo(file);
      return !!info && file.source !== 'working_paper' && !file.workingPaper && this.taskCount(file) === 0;
    },
    passesQuickFilter(file) {
      if (this.quickFilter === 'not_linked') {
        return this.isNotLinked(file);
      }
      if (this.quickFilter === 'not_signed_off') {
        return this.isProjectFile(file) && !this.isAccepted(file);
      }
      return true;
    },
    taskInfo(file) {
      if (file.fromTask) {
        return file;
      }
      if (file.collection_name === 'engagement_evidence_collection') {
        return _.find(this.evidenceFiles, item => item.id === file.id) || null;
      }
      return null;
    },
    taskCount(file) {
      const info = this.taskInfo(file);
      return info ? (info.linked_task_ids || []).length + (info.owner_task ? 1 : 0) : 0;
    },
    signOffState(file) {
      const state = this.engagement.document_sign_offs ? this.engagement.document_sign_offs[file.id] : null;
      return state || { signers: [], rejected: null };
    },
    isAccepted(file) {
      return this.signOffState(file).signers.length >= this.requiredSignOffs;
    },
    isSigner(file) {
      const user = this.$store.state.user;
      return !!(user && _.some(this.signOffState(file).signers, signer => signer.user_id === user.id));
    },
    signersTooltip(file) {
      return _.map(this.signOffState(file).signers, signer => signer.name + ' – ' + this.$options.filters.formatDateTime(signer.signed_at)).join(', ');
    },
    rejectedTooltip(file) {
      const rejected = this.signOffState(file).rejected;
      return rejected.name + ' – ' + this.$options.filters.formatDateTime(rejected.rejected_at) + (rejected.comment ? ': ' + rejected.comment : '');
    },
    showRejectModal(file) {
      this.rejectFile = file;
      this.rejectComment = '';
      this.$refs['reject-document-modal'].show();
    },
    signOff(file, type, comment) {
      let self = this;
      this.buttonDisabled = true;
      piincHttp.post('projects/' + this.project.slug + '/documents/' + file.id + '/sign-off', {
        type: type,
        comment: comment || null
      }).then(function(response) {
        self.buttonDisabled = false;
        if (type === 'rejected') {
          self.$refs['reject-document-modal'].hide();
        }
        self.$toastr('success', self.$t('projects.engagement.documents.' + (type === 'rejected' ? 'reject_success' : 'sign_off_success')));
        self.$emit('changed', response.data);
      }, function(error) {
        self.buttonDisabled = false;
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    },
    canDelete(file) {
      // Files on tasks are removed on the task itself
      if (!this.isOpen || file.fromTask) {
        return false;
      }
      if (file.collection_name === COLLECTION_OTHER) {
        return !!this.access.edit;
      }
      return !!file.phase && canEditPhase(this.project, file.phase);
    },
    upload() {
      let self = this;
      if (!this.files.length) {
        return;
      }

      let formData = new FormData();
      for (let i = 0; i < this.files.length; i++) {
        if (this.files[i].size > 10485760) {
          this.$toastr('error', this.$t('system.file_size'));
          this.files = [];
          return;
        }
        formData.append('document[]', this.files[i].file);
      }

      formData.append('name', 'documents');
      if (this.uploadTarget === WORKING_PAPER_TARGET) {
        // Working papers stay on the main task and are never shown to the client
        formData.append('collection', 'documents');
        formData.append('model_type', 'App\\Project\\Models\\ProjectTask');
        formData.append('model_id', this.workingPaperTask.id);
      } else {
        formData.append('collection', this.uploadTarget || COLLECTION_OTHER);
        formData.append('model_type', this.project.who_am_i);
        formData.append('model_id', this.project.id);
      }

      piincHttp.post('documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(function() {
        self.files = [];
        self.$toastr('success', self.$t('maintenance.third_parties.media_add_success'));
        self.$emit('media-changed');
        // Task files are not part of the project media, so they are reloaded here
        if (self.uploadTarget === WORKING_PAPER_TARGET) {
          self.loadEvidence();
        }
      }, function(error) {
        self.files = [];
        let message = error.data && error.data.message ? error.data.message : '';
        if (error.data && error.data.errors && error.data.errors['document.0']) {
          message += ' ' + error.data.errors['document.0'][0];
        }
        self.$toastr('error', message);
      });
    },
    // Generated exports aren't evidence, so they can't be annotated
    canAnnotate(file) {
      return file.collection_name !== 'attachments' && isAnnotatable(file);
    },
    // From the Documents tab new annotations are general: they show on every task the file is on
    annotate(file) {
      this.$refs.annotationViewer.show({ project: this.project, file: file, task: null });
    },
    opensAnnotated(file) {
      return this.canAnnotate(file) && !!this.annotationCounts[file.id];
    },
    openDocument(file) {
      // Annotated files open in the viewer with their annotations; others in a new browser tab
      if (this.opensAnnotated(file)) {
        this.annotate(file);
        return;
      }
      openDocumentInTab(file);
    },
    downloadMedia(file) {
      piincHttp.get('documents/' + file.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = file.file_name;

        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    openDeleteModal(file) {
      this.selectedFile = file;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.selectedFile = {};
      this.showDeleteModal = false;
      this.$emit('media-changed');
    }
  }
}
</script>
